// Auto-extracted from Profile.jsx
import { useState, useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { C } from "../../lib/theme";
import { Card } from "../../components/Card";
import { Btn } from "../../components/Btn";
import { Eyebrow } from "../../components/Eyebrow";
import { Icon } from "../../components/Icon";
import { Skeleton } from "../../components/Skeleton";
import { EmptyState } from "../../components/EmptyState";
import { ProductCard } from "../../components/ProductCard";
import { supabase } from "../../lib/supabase";
import { fmtK } from "../../lib/money";
import { useWishlist } from "../../store/wishlistStore";
import { getAllLocations } from "../../data/zambianLocations";
import { timeAgo } from "../../lib/dateUtils";
import { useCategories } from "../../hooks/useCategories";
import { BecomeSellerModal } from "../../components/BecomeSellerModal";
import { PhoneVerificationModal } from "../../components/PhoneVerificationModal";
import { DeleteAccountModal } from "../../components/DeleteAccountModal";
import { Field, SecurityRow, InfoRow, inputStyle } from "./shared";
import { StatCard, QuickLink } from "./uiHelpers";

export function CustomerOverview({ user, seller, onGoTo, setActive, onOpenStore, onSellerCreated }) {
  const hasSellerAccount = !!seller;
  const wishlistSize = useWishlist((s) => Object.keys(s.map).length);
  const [becomeOpen, setBecomeOpen] = useState(false);

  const { data: stats = { orders: 0, spent: 0, reviews: 0 }, isLoading: loading } = useQuery({
    queryKey: ["customer-overview", user?.id],
    enabled: !!user?.id,
    staleTime: 60_000,
    queryFn: async () => {
      const [{ data: orders }, { count: reviewCount }] = await Promise.all([
        supabase
          .from("orders")
          .select("total_amount, status")
          .eq("buyer_id", user.id),
        supabase
          .from("reviews")
          .select("id", { count: "exact", head: true })
          .eq("buyer_id", user.id),
      ]);
      // orders.status enum: pending | partial | completed | cancelled.
      // "Spent" excludes only cancelled — anything paid (partial/completed) counts,
      // and we include pending too since the buyer committed the funds at checkout.
      const paid = (orders || []).filter((o) => o.status !== "cancelled");
      const spent = paid.reduce((s, o) => s + (o.total_amount || 0), 0);
      return { orders: (orders || []).length, spent, reviews: reviewCount || 0 };
    },
  });

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <Card pad={22}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <div
            style={{
              width: 72,
              height: 72,
              borderRadius: 18,
              background: user?.avatar_url ? `url(${user.avatar_url}) center/cover` : C.hunter,
              color: C.dust,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 28,
              fontWeight: 900,
              flexShrink: 0,
            }}
          >
            {!user?.avatar_url && (user?.name?.[0] || "K").toUpperCase()}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 20, fontWeight: 900, color: C.charcoal }}>{user?.name || "You"}</div>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
              {user?.email}
              {user?.universities?.short_name ? ` · ${user.universities.short_name}` : ""}
            </div>
            {user?.phone && (
              <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
                {user.phone} {user?.phone_verified ? "· Verified" : "· Unverified"}
              </div>
            )}
          </div>
          <Btn size="sm" kind="outline" onClick={() => onGoTo("account")}>Edit profile</Btn>
        </div>
      </Card>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
        <StatCard label="Orders" value={loading ? "—" : stats.orders} icon="package" />
        <StatCard label="Total spent" value={loading ? "—" : fmtK(stats.spent)} icon="wallet" />
        <StatCard label="Reviews" value={loading ? "—" : stats.reviews} icon="star" />
        <StatCard label="Wishlist" value={wishlistSize} icon="heart" />
      </div>

      <Card pad={22}>
        <Eyebrow>Quick actions</Eyebrow>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginTop: 10 }}>
          <QuickLink icon="package" label="My orders" onClick={() => setActive?.("orders")} />
          <QuickLink icon="heart" label="My wishlist" onClick={() => onGoTo("wishlist")} />
          <QuickLink icon="message-square" label="My messages" onClick={() => setActive?.("messages")} />
          <QuickLink icon="map-pin" label="Addresses" onClick={() => onGoTo("addresses")} />
          <QuickLink icon="credit-card" label="Payment methods" onClick={() => onGoTo("payments")} />
          <QuickLink icon="help-circle" label="Get help" onClick={() => onGoTo("support")} />
        </div>
      </Card>

      {hasSellerAccount ? (
        <Card pad={22} style={{ background: C.hunter, color: C.dust, border: "none" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: 16,
                background: seller.logo_url ? `url(${seller.logo_url}) center/cover` : "rgba(218, 215, 205, 0.15)",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {!seller.logo_url && <Icon name="store" size={24} color={C.dust} />}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 16, fontWeight: 900, color: C.dust, marginBottom: 4 }}>
                {seller.store_name || "Your store"}
              </div>
              <div style={{ fontSize: 12, color: C.dust, opacity: 0.8, lineHeight: 1.5 }}>
                Switch to Sell mode to manage products, orders, and payouts.
              </div>
            </div>
            <Btn size="md" kind="dust" onClick={() => onOpenStore?.()}>
              Open store
            </Btn>
          </div>
        </Card>
      ) : (
        <>
          <Card pad={22} style={{ background: C.hunter, color: C.dust, border: "none" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
              <div
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: 16,
                  background: "rgba(218, 215, 205, 0.15)",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Icon name="store" size={24} color={C.dust} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 16, fontWeight: 900, color: C.dust, marginBottom: 4 }}>
                  Open a store on Kapio
                </div>
                <div style={{ fontSize: 12, color: C.dust, opacity: 0.8, lineHeight: 1.5 }}>
                  Sell to thousands of students with escrow buyer protection, built-in chat,
                  and on-demand payouts. Admin review takes ~1 business day.
                </div>
              </div>
              <Btn size="md" kind="dust" onClick={() => setBecomeOpen(true)}>
                Become a seller
              </Btn>
            </div>
          </Card>

          <BecomeSellerModal
            open={becomeOpen}
            onClose={() => setBecomeOpen(false)}
            userId={user?.id}
            onSuccess={async () => {
              setBecomeOpen(false);
              await onSellerCreated?.();
            }}
          />
        </>
      )}
    </div>
  );
}
